import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TOKEN_KEY = 'transvie_admin_token';

const MAIN_COLUMNS = [
  ['createdAt', 'date'],
  ['decision', 'decision'],
  ['req_reference_number', 'reference'],
  ['req_amount', 'amount'],
  ['req_currency', 'currency'],
  ['req_bill_to_forename', 'firstName'],
  ['req_bill_to_surname', 'lastName'],
  ['req_bill_to_email', 'email'],
  ['req_bill_to_phone', 'phone'],
];

const EXTRA_COLUMNS = [
  ['req_bill_to_address_country', 'country'],
  ['req_bill_to_address_state', 'region'],
  ['req_bill_to_address_line1', 'address'],
  ['req_bill_to_address_postal_code', 'postalCode'],
  ['reference_first_name', 'contactFirstName'],
  ['reference_last_name', 'contactLastName'],
  ['reference_phone', 'contactPhone'],
  ['card_type_name', 'card'],
  ['req_card_number', 'cardNumber'],
  ['transaction_id', 'transaction'],
  ['auth_code', 'authCode'],
  ['message', 'message'],
];

function formatCell(key, value) {
  if (value == null || value === '') return '';
  if (key === 'createdAt') {
    return new Date(value).toLocaleString('fr-FR');
  }
  if (key === 'req_amount') return String(value);
  return String(value);
}

export default function Adhesions() {
  const { t } = useTranslation();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [firstName, setFirstName] = useState('');
  const [date, setDate] = useState('');
  const [query, setQuery] = useState({ firstName: '', date: '' });
  const [openId, setOpenId] = useState(null);

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3005/api';

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ page: String(page) });
    if (query.firstName) params.set('firstName', query.firstName);
    if (query.date) params.set('date', query.date);
    fetch(`${apiBase}/admin/adhesions?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (response.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          if (!cancelled) setToken('');
          return null;
        }
        if (!response.ok) throw new Error('load');
        return response.json();
      })
      .then((data) => {
        if (!cancelled && data) {
          setRows(data.rows || []);
          setTotal(data.total || 0);
          setPageSize(data.pageSize || 15);
        }
      })
      .catch(() => {
        if (!cancelled) setError(t('admin.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, apiBase, t, page, query]);

  async function handleLogin(event) {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!response.ok) {
        setError(t('admin.loginError'));
        return;
      }
      const data = await response.json();
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword('');
    } catch {
      setError(t('admin.loginError'));
    } finally {
      setLoading(false);
    }
  }

  function handleLogout() {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setRows(null);
    setOpenId(null);
    setPage(1);
    setQuery({ firstName: '', date: '' });
  }

  function handleSearch(event) {
    event.preventDefault();
    setPage(1);
    setOpenId(null);
    setQuery({ firstName: firstName.trim(), date });
  }

  function handleReset() {
    setFirstName('');
    setDate('');
    setPage(1);
    setOpenId(null);
    setQuery({ firstName: '', date: '' });
  }

  if (!token) {
    return (
      <section style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '160px 16px 48px' }}>
        <form onSubmit={handleLogin} style={{ width: 'min(420px, 100%)', background: '#fff', border: '1px solid #e6e6e6', borderRadius: 16, padding: 28 }}>
          <h1 style={{ fontSize: 28, marginBottom: 8 }}>{t('admin.title')}</h1>
          <p style={{ color: '#666', marginBottom: 24 }}>{t('admin.subtitle')}</p>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>{t('admin.username')}</label>
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            style={{ width: '100%', height: 48, border: '2px solid #ddd', borderRadius: 8, padding: '0 12px', marginBottom: 16 }}
          />
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>{t('admin.password')}</label>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            style={{ width: '100%', height: 48, border: '2px solid #ddd', borderRadius: 8, padding: '0 12px', marginBottom: 16 }}
          />
          {error && <p style={{ color: '#dc3545', marginBottom: 12 }}>{error}</p>}
          <button type="submit" disabled={loading} style={{ width: '100%', height: 48, border: 0, borderRadius: 8, background: '#0171CE', color: '#fff', fontWeight: 700 }}>
            {t('admin.submit')}
          </button>
        </form>
      </section>
    );
  }

  return (
    <section style={{ padding: '150px 20px 48px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 28, margin: 0 }}>{t('admin.tableTitle', { count: total })}</h1>
        <button type="button" onClick={handleLogout} style={{ height: 40, padding: '0 16px', borderRadius: 8, border: '1px solid #0171CE', background: '#fff', color: '#0171CE', fontWeight: 700 }}>
          {t('admin.logout')}
        </button>
      </div>
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end', marginBottom: 16 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600 }}>
          {t('admin.searchFirstName')}
          <input value={firstName} onChange={(event) => setFirstName(event.target.value)} style={{ height: 40, border: '2px solid #ddd', borderRadius: 8, padding: '0 12px', fontWeight: 400 }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600 }}>
          {t('admin.searchDate')}
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} style={{ height: 40, border: '2px solid #ddd', borderRadius: 8, padding: '0 12px', fontWeight: 400 }} />
        </label>
        <button type="submit" style={{ height: 40, padding: '0 16px', border: 0, borderRadius: 8, background: '#0171CE', color: '#fff', fontWeight: 700 }}>
          {t('admin.search')}
        </button>
        <button type="button" onClick={handleReset} style={{ height: 40, padding: '0 16px', borderRadius: 8, border: '1px solid #0171CE', background: '#fff', color: '#0171CE', fontWeight: 700 }}>
          {t('admin.reset')}
        </button>
      </form>
      {error && <p style={{ color: '#dc3545' }}>{error}</p>}
      {loading && <p>{t('admin.loading')}</p>}
      {rows && rows.length === 0 && <p>{query.firstName || query.date ? t('admin.noResult') : t('admin.empty')}</p>}
      {rows && rows.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #e6e6e6', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={{ width: 110, padding: '12px 10px', background: '#0171CE' }} />
                {MAIN_COLUMNS.map(([, label]) => (
                  <th key={label} style={{ textAlign: 'left', padding: '12px 10px', background: '#0171CE', color: '#fff', whiteSpace: 'nowrap' }}>
                    {t(`admin.columns.${label}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const open = openId === row.id;
                return (
                  <Fragment key={row.id}>
                    <tr>
                      <td style={{ padding: '8px 10px', borderTop: '1px solid #eee' }}>
                        <button
                          type="button"
                          aria-expanded={open}
                          onClick={() => setOpenId(open ? null : row.id)}
                          style={{ border: 0, background: 'transparent', color: '#0171CE', fontWeight: 700, cursor: 'pointer', padding: 0 }}
                        >
                          {open ? t('admin.collapse') : t('admin.expand')}
                        </button>
                      </td>
                      {MAIN_COLUMNS.map(([key]) => (
                        <td key={key} style={{ padding: '10px', borderTop: '1px solid #eee', color: key === 'decision' && row.decision !== 'ACCEPT' ? '#dc3545' : '#222' }}>
                          {formatCell(key, row[key])}
                        </td>
                      ))}
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={MAIN_COLUMNS.length + 1} style={{ padding: 16, background: '#f6f9fc', borderTop: '1px solid #eee' }}>
                          <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px 20px', margin: 0 }}>
                            {EXTRA_COLUMNS.map(([key, label]) => (
                              <div key={key}>
                                <dt style={{ fontSize: 12, color: '#667', marginBottom: 2 }}>{t(`admin.columns.${label}`)}</dt>
                                <dd style={{ margin: 0, fontWeight: 600 }}>{formatCell(key, row[key]) || '—'}</dd>
                              </div>
                            ))}
                          </dl>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {total > pageSize && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
          <button type="button" disabled={page <= 1} onClick={() => { setOpenId(null); setPage((current) => current - 1); }} style={{ height: 40, padding: '0 16px', borderRadius: 8, border: '1px solid #0171CE', background: '#fff', color: '#0171CE', fontWeight: 700 }}>
            {t('admin.prev')}
          </button>
          <span>{t('admin.page', { page, pages: Math.ceil(total / pageSize) })}</span>
          <button type="button" disabled={page >= Math.ceil(total / pageSize)} onClick={() => { setOpenId(null); setPage((current) => current + 1); }} style={{ height: 40, padding: '0 16px', borderRadius: 8, border: '1px solid #0171CE', background: '#fff', color: '#0171CE', fontWeight: 700 }}>
            {t('admin.next')}
          </button>
        </div>
      )}
    </section>
  );
}
